import { useEffect, useMemo, useRef, useState } from "react"
import { type ArtistoInRegia, type StatoRegia, apiRegia } from "../../api/regia"
import { useLargo } from "../../stato/useLargo"

interface Props {
  stato: StatoRegia
  codice: string
  onSerata: (id: string) => void
  comando: (azione: () => Promise<unknown>) => Promise<void>
}

export function Diretta({ stato, codice, onSerata, comando }: Props) {
  const [filtro, setFiltro] = useState("")
  const cerca = useRef<HTMLInputElement>(null)
  const largo = useLargo()

  /** La scaletta non si compone: si scopre. Qui è l'elenco di chi è già
   *  passato, nell'ordine in cui è stato davvero aperto. */
  const scaletta = useMemo(
    () =>
      stato.artisti
        .filter((a) => a.stato != null)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [stato.artisti],
  )

  const daAprire = useMemo(() => {
    const q = filtro.trim().toLowerCase()
    return q ? stato.artisti.filter((a) => a.name.toLowerCase().includes(q)) : stato.artisti
  }, [stato.artisti, filtro])

  // Alla tastiera "/" porta dritti alla ricerca, come su tanti siti. Invio
  // invece non apre nessuno: un'apertura sbagliata suona su otto telefoni.
  useEffect(() => {
    function tasto(ev: KeyboardEvent) {
      if (ev.key !== "/" || ev.metaKey || ev.ctrlKey || ev.altKey) return
      const origine = ev.target
      if (origine instanceof Element && origine.closest("input, textarea, select, [contenteditable]"))
        return
      ev.preventDefault()
      cerca.current?.focus()
      cerca.current?.select()
    }
    document.addEventListener("keydown", tasto)
    return () => document.removeEventListener("keydown", tasto)
  }, [])

  return (
    <div className="diretta">
      <nav className="regia__serate" aria-label="Serata">
        {stato.serate.map((s) => (
          <button
            key={s.id}
            type="button"
            className="regia__serata"
            aria-current={s.id === stato.serata.id}
            data-stato={s.status}
            onClick={() => onSerata(s.id)}
          >
            {s.number}
          </button>
        ))}
      </nav>

      <Adesso
        aperta={stato.aperta}
        largo={largo}
        onChiudi={() => comando(() => apiRegia.chiudiVoti(stato.aperta!.performance_id, codice))}
        onSvela={() => comando(() => apiRegia.svela(stato.aperta!.performance_id, codice))}
      />

      <section className="regia__scelta">
        <label className="occhiello" htmlFor="cerca">
          Chi canta adesso
        </label>
        <div className="regia__cerca">
          <input
            id="cerca"
            ref={cerca}
            className="campo"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setFiltro("")
            }}
            placeholder="Cerca un artista"
            autoComplete="off"
          />
          {largo && !filtro && (
            <kbd className="regia__tasto" aria-hidden="true">
              /
            </kbd>
          )}
        </div>
        {stato.artisti.length === 0 && (
          <p className="regia__vuoto">
            Questa edizione non ha ancora artisti. Aggiungili dalla scheda <b>Artisti</b>.
          </p>
        )}
        <ul className="regia__artisti">
          {daAprire.map((a) => (
            <Riga
              key={a.id}
              artista={a}
              inCorso={a.id === stato.aperta?.artist_id}
              onApri={() => comando(() => apiRegia.apri(stato.serata.id, a.id, codice))}
            />
          ))}
        </ul>
      </section>

      {scaletta.length > 0 ? (
        <section className="regia__riepilogo">
          <p className="occhiello">
            Scaletta della serata {stato.serata.number} · {scaletta.length}{" "}
            {scaletta.length === 1 ? "esibizione" : "esibizioni"}
          </p>
          <ol className="regia__scaletta">
            {scaletta.map((a) => (
              <li key={a.id} data-stato={a.stato ?? undefined}>
                <span className="regia__posizione">{a.position}</span>
                <span className="regia__nome">{a.name}</span>
                <span className="regia__stato">{ETICHETTA[a.stato ?? ""]}</span>
              </li>
            ))}
          </ol>
        </section>
      ) : (
        // Al telefono una scaletta vuota non occupa posto; sul portatile la
        // sua colonna c'è comunque, e vuota sembrerebbe rotta.
        largo && (
          <section className="regia__riepilogo">
            <p className="occhiello">Scaletta della serata {stato.serata.number}</p>
            <p className="regia__vuoto">Si riempie da sola man mano che apri le esibizioni.</p>
          </section>
        )
      )}

      <Delicate stato={stato} codice={codice} comando={comando} />
    </div>
  )
}

/** Chi sta cantando, e soprattutto chi manca all'appello. È la sola cosa
 *  che deve essere leggibile in un colpo d'occhio. */
function Adesso({
  aperta,
  largo,
  onChiudi,
  onSvela,
}: {
  aperta: StatoRegia["aperta"]
  largo: boolean
  onChiudi: () => void
  onSvela: () => void
}) {
  if (!aperta) {
    const dove = largo ? "a sinistra" : "qui sotto"
    return (
      <div className="adesso adesso--vuoto">
        <p className="occhiello">Adesso</p>
        <p className="adesso__nessuno">Nessuna esibizione aperta</p>
        <p className="adesso__nota">Scegli dall'elenco {dove} chi sta cantando.</p>
      </div>
    )
  }

  return (
    <div className="adesso">
      <p className="occhiello">Adesso · {aperta.position}ª esibizione</p>
      <h2 className="adesso__nome">{aperta.name}</h2>

      <p className="adesso__conteggio">
        {aperta.voted.length} su {aperta.voted.length + aperta.pending.length} hanno votato
      </p>
      {aperta.pending.length > 0 ? (
        <>
          {largo && <p className="adesso__etichetta">Mancano</p>}
          <ul className="adesso__mancanti">
            {aperta.pending.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </>
      ) : (
        <p className="adesso__tutti">Hanno votato tutti: si svela da solo.</p>
      )}
      {/* C'è spazio per dire anche chi ha già votato: aiuta a capire a colpo
          d'occhio se chi manca è distratto o se è la serata a essere lenta. */}
      {largo && aperta.voted.length > 0 && aperta.pending.length > 0 && (
        <>
          <p className="adesso__etichetta">Hanno già votato</p>
          <ul className="adesso__mancanti adesso__votanti">
            {aperta.voted.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </>
      )}

      <div className="adesso__comandi">
        <button type="button" className="bottone" onClick={onChiudi}>
          Chiudi i voti
        </button>
        <button type="button" className="bottone primario" onClick={onSvela}>
          Svela adesso
        </button>
      </div>
    </div>
  )
}

const ETICHETTA: Record<string, string> = {
  pending: "in scaletta",
  voting: "aperta",
  locked: "voti chiusi",
  revealed: "svelata",
}

function Riga({
  artista,
  inCorso,
  onApri,
}: {
  artista: ArtistoInRegia
  inCorso: boolean
  onApri: () => void
}) {
  return (
    <li>
      <button
        type="button"
        className="regia__riga"
        data-stato={artista.stato ?? "mai"}
        aria-current={inCorso}
        disabled={artista.stato === "revealed"}
        onClick={onApri}
      >
        <span className="regia__posizione">{artista.position ?? "·"}</span>
        <span className="regia__nome">
          {artista.name}
          {artista.song && <small>{artista.song}</small>}
        </span>
        <span className="regia__stato">
          {artista.stato ? ETICHETTA[artista.stato] : "non ancora"}
        </span>
      </button>
    </li>
  )
}

/** Separate dalle azioni frequenti, chiuse per default e dietro conferma:
 *  un tocco sbagliato qui rovina la serata a otto persone. */
function Delicate({
  stato,
  codice,
  comando,
}: {
  stato: StatoRegia
  codice: string
  comando: (azione: () => Promise<unknown>) => Promise<void>
}) {
  const [conferma, setConferma] = useState<string | null>(null)

  const annullabili = stato.artisti
    .filter((a) => a.performance_id && a.stato !== "revealed")
    .sort((a, b) => (b.position ?? 0) - (a.position ?? 0))

  return (
    <details className="delicate">
      <summary>Azioni delicate</summary>

      {annullabili.length > 0 && (
        <>
          <p className="delicate__nota">
            Aperto l'artista sbagliato? Annullare toglie l'esibizione dalla serata insieme ai voti
            che ha raccolto. Una già svelata non si annulla.
          </p>
          <ul className="delicate__elenco">
            {annullabili.map((a) => (
              <li key={a.id}>
                <span>
                  {a.position}. {a.name} <small>{ETICHETTA[a.stato ?? ""]}</small>
                </span>
                {conferma === a.id ? (
                  <span className="delicate__conferma">
                    <button type="button" className="bottone" onClick={() => setConferma(null)}>
                      No
                    </button>
                    <button
                      type="button"
                      className="bottone delicate__rossa"
                      onClick={() => {
                        setConferma(null)
                        void comando(() => apiRegia.annulla(a.performance_id!, codice))
                      }}
                    >
                      Annulla {a.name}
                    </button>
                  </span>
                ) : (
                  <button type="button" className="bottone" onClick={() => setConferma(a.id)}>
                    Annulla
                  </button>
                )}
              </li>
            ))}
          </ul>
          <hr className="delicate__riga" />
        </>
      )}

      {stato.serata.status === "closed" ? (
        <p className="delicate__nota">La serata {stato.serata.number} è già chiusa.</p>
      ) : (
        <>
          <p className="delicate__nota">
            Chiudere la serata {stato.serata.number} blocca i voti ancora aperti e la toglie dalla
            diretta.
          </p>
          {conferma === "serata" ? (
            <div className="delicate__conferma">
              <button type="button" className="bottone" onClick={() => setConferma(null)}>
                Lascia stare
              </button>
              <button
                type="button"
                className="bottone delicate__rossa"
                onClick={() => {
                  setConferma(null)
                  void comando(() => apiRegia.chiudiSerata(stato.serata.id, codice))
                }}
              >
                Sì, chiudi la serata {stato.serata.number}
              </button>
            </div>
          ) : (
            <button type="button" className="bottone" onClick={() => setConferma("serata")}>
              Chiudi la serata {stato.serata.number}
            </button>
          )}
        </>
      )}
    </details>
  )
}
